import { isNative, platform } from '~/plugins/native/capacitor'

// Handle iOS Live Activity widgets
export default async function ({ store, $log, $api, $dateNew, $ApiModel }, inject) {
  const liveActivity = {
    isAvailable: false,
    isRunning: () => false,
    requestTokens: () => {}
  }

  if (isNative && platform === 'ios') {
    const { LiveActivity } = await import('capacitor-live-activity')
    const { App } = await import('@capacitor/app')

    const isAvailable = await LiveActivity.isAvailable()

    const debugLog = (type, data = {}) => {
      store.commit('debug/addLogEntry', { type, ...data })
    }

    $log.debug('Live Activity availability', isAvailable.value)

    if (isAvailable.value) {
      liveActivity.isAvailable = true

      liveActivity.dismissStaleActivity = async () => {
        try {
          // Try listActivities first, fall back to getCurrentActivity
          const { activities } = await LiveActivity.listActivities()
          let activeActivities = activities?.filter(a => a.state === 'active' || a.state === 'stale') || []

          // If listActivities returns empty, check the in-memory dictionary via getCurrentActivity
          if (activeActivities.length === 0) {
            const current = await liveActivity.getRunningId()

            if (current?.id) {
              // getCurrentActivity returns system UUID as id — get booking ID from content state values
              const bookingId = current.values?.bookingId || current.values?.id || null

              activeActivities = [
                { id: bookingId, activityId: current.id, state: current.isStale ? 'stale' : 'active' }
              ]
            }
          }

          debugLog('dismiss-check', {
            activities: activeActivities.map(a => ({ id: a.id, activityId: a.activityId, state: a.state }))
          })

          if (activeActivities.length === 0) return

          // Fetch fresh booking data before checking if a booking is running
          await store.dispatch('booking/fetchRecentBookings')

          const bookingRunningNow = store.getters['booking/bookingRunningNow']

          if (!bookingRunningNow) {
            $log.debug('No active booking but live activity running, dismissing widgets')

            const dismissalDate = $dateNew.now().plus({ seconds: 2 }).toUnixInteger()

            for (const activity of activeActivities) {
              if (!activity.id) {
                debugLog('dismiss-stale-skip', { reason: 'no booking ID', activityId: activity.activityId })
                continue
              }

              debugLog('dismiss-stale', { id: activity.id, activityId: activity.activityId })

              await LiveActivity.endActivity({
                id: activity.id,
                contentState: {
                  hasEnded: 'true',
                  startAt: '',
                  endAt: '',
                  paddockName: '',
                  endString: '',
                  deepLinkUrl: '',
                  bookingId: '',
                  id: ''
                },
                dismissalDate
              })

              debugLog('dismiss-stale-success', { id: activity.id })
            }

            $log.debug('Stale live activities dismissed successfully')
          }
        } catch (error) {
          debugLog('dismiss-stale-error', { error: error?.message || String(error) })
          $log.debug('Error dismissing stale live activity', error)
        }
      }

      liveActivity.listenForTokens = () => {
        try {
          App.addListener('appStateChange', appState => {
            console.log({ appState })

            if (appState?.isActive) {
              liveActivity.requestTokens()
              liveActivity.dismissStaleActivity()
            }
          })

          LiveActivity.addListener('liveActivityPushToStartToken', async event => {
            $log.debug('Received live activity push start token', event)
            debugLog('push-to-start-token', { token: event.token?.slice(-8) || null })

            const app = store.getters['app/getApp']

            if (app.isAuth && event.token) {
              const apiModel = new $ApiModel()

              await $api.user(apiModel).setPushStartToken(event.token)
            }

            // console.log('Checking for running live activity to send booking token to server')

            // const runningActivity = await LiveActivity.getCurrentActivity()

            // console.log(JSON.stringify(runningActivity))
          })

          LiveActivity.addListener('liveActivityPushToken', async ({ id, token }) => {
            $log.debug('Received live activity push BOOKING token', id, token)
            debugLog('booking-push-token', { bookingId: id, token: token?.slice(-8) || null })

            // Send token to server
            const bookingId = id

            const app = store.getters['app/getApp']

            if (app.isAuth && bookingId && token) {
              const apiModel = new $ApiModel()

              await $api.booking(apiModel).setBookingPushToken(bookingId, token)
            }
          })
        } catch (error) {
          $log.debug('Error dealing with live activity tokens', error)
        }
      }

      let hasObservedPushToStartToken = false

      liveActivity.requestTokens = async () => {
        try {
          if (hasObservedPushToStartToken) return

          if (store.getters['notifications/isPushNotificationsReady']) {
            hasObservedPushToStartToken = true
            await LiveActivity.observePushToStartToken()
          } else {
            console.log('Push notifications not ready, cannot request live activity push start token')
          }
        } catch (error) {
          $log.debug('Error requesting live activity push start token', error)
        }
      }

      window.onNuxtReady(() => {
        liveActivity.listenForTokens()
        liveActivity.requestTokens()

        // Delay to let the plugin init discover existing activities
        setTimeout(() => {
          liveActivity.dismissStaleActivity()
        }, 2000)
      })

      liveActivity.getRunningId = async () => {
        try {
          const result = await LiveActivity.getCurrentActivity()

          if (!result?.id) return null

          return {
            id: result.id,
            isStale: result.isStale,
            isEnded: result.isEnded,
            startedAt: result.startedAt,
            values: result.values
          }
        } catch (error) {
          $log.debug('Error with live activity get current activity', error)
          return null
        }
      }

      liveActivity.isRunning = async () => {
        try {
          const activity = await liveActivity.getRunningId()

          console.log('liveActivity activity', activity)

          if (activity?.id) {
            $log.debug('Live activity is running with id', activity.id)
            return true
          } else {
            $log.debug('No live activity running')
            return false
          }
        } catch (error) {
          $log.debug('Error checking if live activity is running', error)
          return false
        }
      }

      liveActivity.start = async ({ bookingId, startAt, endAt, paddockName }) => {
        try {
          const currentActivity = await liveActivity.getRunningId()

          const endAtDate = $dateNew.fromSQL(endAt)
          const diff = endAtDate.diff($dateNew.now(), ['minutes', 'seconds']).toObject()

          const minutes = diff.minutes ? String(Math.floor(diff.minutes)).padStart(2, '0') : '00'
          const seconds = diff.seconds ? String(Math.floor(diff.seconds)).padStart(2, '0') : '00'

          let timeRemaining = `${minutes}:${seconds}`

          if ($dateNew.now() > endAtDate) {
            timeRemaining = '00:00'
          }

          $log.debug('currentActivity', JSON.stringify(currentActivity))

          const deepLinkUrl = `${process.env.NATIVE_APP_ID}://platform/booking/${bookingId}/session`

          const hasEnded = $dateNew.now().plus({ minutes: 1 }) > endAtDate

          const contentState = {
            paddockName,
            timeRemaining,
            startAt,
            endAt,
            deepLinkUrl,
            endString: endAtDate.toLocaleString($dateNew.TIME_24_SIMPLE),
            hasEnded: hasEnded ? 'true' : 'false'
          }

          $log.debug('contentState', JSON.stringify(contentState))

          if (currentActivity?.id) {
            $log.debug('Live activity already running', bookingId)
            debugLog('start-already-running', { bookingId, hasEnded: contentState.hasEnded })

            if (contentState.hasEnded === 'true') {
              $log.debug('Ending live actvity', bookingId)
              // epoch in seconds, 10 minutes from now
              const dismissalDate = $dateNew.now().plus({ minutes: 10 }).toUnixInteger()

              $log.debug('dismissalDate', dismissalDate)

              await LiveActivity.endActivity({
                id: bookingId,
                contentState,
                dismissalDate
              })

              return
            }

            await LiveActivity.updateActivity({
              id: bookingId,
              contentState,
              alert
            })

            $log.debug('Updated live activity', bookingId)
          } else if (!hasEnded) {
            await LiveActivity.startActivityWithPush({
              id: bookingId,
              attributes: {},
              contentState
            })

            debugLog('start-local', { bookingId, contentState })
            $log.debug('Live activity started', bookingId)
          }

          //   const dismissalDate = new Date(new Date().getTime() + 10000)

          //   console.log('dismissalDate', dismissalDate)

          //   // LiveActivity.endActivity({
          //   //   id: 'test',
          //   //   dismissalDate
          //   // })
        } catch (error) {
          $log.error('Error starting live activity', error?.message || error)
        }
      }

      liveActivity.end = async bookingId => {
        try {
          const isRunning = await liveActivity.isRunning()

          if (isRunning) {
            $log.debug('Live activity ending', bookingId)
            const dismissalDate = $dateNew.now().plus({ minutes: 2 }).toUnixInteger()

            console.log('dismissalDate', dismissalDate)
            try {
              await LiveActivity.endActivity({
                id: bookingId,
                contentState: {
                  hasEnded: 'true',
                  startAt: '',
                  endAt: '',
                  paddockName: '',
                  endString: '',
                  deepLinkUrl: '',
                  bookingId: bookingId || '',
                  id: bookingId || ''
                },
                dismissalDate
              })

              $log.debug('Live activity ended successfully', bookingId)
            } catch (error) {
              $log.error('Error ending existing live activity', error?.message || error)
            }
          } else {
            $log.debug('No live activity to end', bookingId)
          }
        } catch (error) {
          $log.debug('Error checking if live activity is running', error)
        }
      }
    }
  }

  inject('liveActivity', liveActivity)
}
